// src/App.jsx
import 'react-quill/dist/quill.snow.css'; // React-Quill CSS
import './styles/dzban-theme.css';
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { db, auth } from './firebase';
import { onAuthStateChanged, signInAnonymously } from 'firebase/auth';
import { collection, query, orderBy, onSnapshot, doc, getDoc, setDoc } from 'firebase/firestore';

// Config
import './config/leaflet';
import { FEATURES } from './config/features';

// Modals
import { ZoomModal } from './components/modals/ZoomModal';
import { Toast } from './components/modals/Toast';
import { ReportModal } from './components/modals/ReportModal';
import { WelcomeModal } from './components/modals/WelcomeModal';

// Layout
import { Sidebar } from './components/layout/Sidebar';
import { MobileNav } from './components/layout/MobileNav';

// Spotting Pages
import { Home } from './features/spotting/pages/Home';
import { ExclusiveGarage } from './features/spotting/pages/ExclusiveGarage';
import { AlertZone } from './features/spotting/pages/AlertZone';
import { SightingDetail } from './features/spotting/pages/SightingDetail';
import { AddSighting } from './features/spotting/pages/AddSighting';
import { PlateAggregation } from './features/spotting/pages/PlateAggregation'; // NOWY!

// Marketplace Pages
import { MarketHome } from './features/marketplace/pages/MarketHome';
import { SearchResults } from './features/marketplace/pages/SearchResults';
import { ListingDetail } from './features/marketplace/pages/ListingDetail';
import { AddListing } from './features/marketplace/pages/AddListing';
import { MyListings } from './features/marketplace/pages/MyListings';
import { Messages } from './features/marketplace/pages/Messages';

// Shared Pages
import { AdminPanel } from './pages/AdminPanel';
import { Login } from './pages/Login';
import { Terms } from './pages/Terms';

// Dzban Roku (conditional - only if enabled)
import { DzbanHome } from './features/dzban/pages/DzbanHome';
import { DzbanRanking } from './features/dzban/pages/DzbanRanking';
import { DzbanCities } from './features/dzban/pages/DzbanCities';
import { DzbanBrands } from './features/dzban/pages/DzbanBrands';

// Floating button do Live Mapy - tylko mobile, tylko na stronie głównej
function MapFloatingButton() {
  const location = useLocation();
  if (location.pathname !== '/') return null;

  return (
    <a
      href="https://mapa.motoradar24.pl"
      target="_blank"
      rel="noopener noreferrer"
      className="lg:hidden fixed bottom-20 right-4 z-40 flex items-center gap-1.5 bg-gradient-to-br from-slate-900 to-red-950 border-2 border-red-500 rounded-full pl-2.5 pr-3 py-2.5 shadow-2xl active:scale-95 transition-transform"
      aria-label="Otwórz Live Mapę"
    >
      <span className="text-base">🗺️</span>
      <span className="flex items-center gap-1 text-[10px] font-bold bg-red-600 text-white px-1.5 py-0.5 rounded-full">
        <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
        LIVE
      </span>
    </a>
  );
}

export default function App() {
  const [sightings, setSightings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [karma, setKarma] = useState(0);
  const [toast, setToast] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [reportId, setReportId] = useState(null);
  const [zoomImg, setZoomImg] = useState(null);
  const [hasUnreadAlerts, setHasUnreadAlerts] = useState(false);
  const [lastAlertCheck, setLastAlertCheck] = useState(null);
  const [showWelcome, setShowWelcome] = useState(false);

  const showToast = (message, type = 'success') => setToast({ message, type });

  // Check if first visit
  useEffect(() => {
    const hasVisited = localStorage.getItem('carwatch_visited');
    if (!hasVisited) {
      setShowWelcome(true);
    }
  }, []);

  // Load site name
  useEffect(() => {
    const loadSiteName = async () => {
      try {
        const settingsDoc = await getDoc(doc(db, "settings", "site"));
        if (settingsDoc.exists() && settingsDoc.data().name) {
          document.title = settingsDoc.data().name;
        }
      } catch (error) {
        console.error("Błąd ładowania nazwy:", error);
      }
    };
    loadSiteName();
  }, []);

  useEffect(() => {
    onAuthStateChanged(auth, async (user) => { 
      if (!user) await signInAnonymously(auth); 
      if (user) {
        onSnapshot(doc(db, "users", user.uid), (d) => setKarma(d.data()?.points || 0));
        
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          setLastAlertCheck(userDoc.data()?.lastAlertCheck || null);
        }
      }
    });
    
    const q = query(collection(db, "sightings"), orderBy("createdAt", "desc"));
    return onSnapshot(q, (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setSightings(data);
      setLoading(false);
      
      if (lastAlertCheck) {
        const newAlerts = data.filter(s => 
          s.isAlert && 
          s.createdAt && 
          s.createdAt.toDate() > new Date(lastAlertCheck)
        );
        setHasUnreadAlerts(newAlerts.length > 0);
      } else {
        setHasUnreadAlerts(data.some(s => s.isAlert));
      }
    });
  }, [lastAlertCheck]);

  const markAlertsAsRead = async () => {
    if (auth.currentUser) {
      await setDoc(doc(db, "users", auth.currentUser.uid), {
        lastAlertCheck: new Date().toISOString()
      }, { merge: true });
      setLastAlertCheck(new Date().toISOString());
      setHasUnreadAlerts(false);
    }
  };

  const handleWelcomeClose = () => {
    localStorage.setItem('carwatch_visited', 'true');
    setShowWelcome(false);
  };

  const handleWelcomeExit = () => {
    window.location.href = 'https://www.google.com';
  };

  return (
    <Router>
      <div className="flex min-h-screen bg-gray-50">
        {/* Modals */}
        {showWelcome && (
          <WelcomeModal 
            onClose={handleWelcomeClose} 
            onExit={handleWelcomeExit}
          />
        )}
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
        {reportId && <ReportModal sightingId={reportId} onClose={() => setReportId(null)} showToast={showToast} />}
        {zoomImg && <ZoomModal imageUrl={zoomImg} onClose={() => setZoomImg(null)} />}
        
        {/* Sidebar */}
        <Sidebar 
          karma={karma} 
          collapsed={collapsed} 
          setCollapsed={setCollapsed} 
          sightings={sightings}
          hasUnreadAlerts={hasUnreadAlerts}
          markAlertsAsRead={markAlertsAsRead}
        />

        {/* Main Content */}
        <main className="flex-1 h-screen overflow-y-auto overflow-x-hidden custom-scrollbar">
          <MapFloatingButton />
          <Routes>
            {/* Spotting Routes */}
            <Route path="/" element={
              <Home 
                sightings={sightings} 
                loading={loading} 
                showToast={showToast} 
                setReportId={setReportId} 
                setZoomImg={setZoomImg} 
              />
            } />
            <Route path="/exclusive" element={
              <ExclusiveGarage 
                sightings={sightings} 
                loading={loading} 
                showToast={showToast} 
                setReportId={setReportId} 
                setZoomImg={setZoomImg} 
              />
            } />
            <Route path="/alert" element={
              <AlertZone 
                sightings={sightings} 
                loading={loading} 
                showToast={showToast} 
                setReportId={setReportId} 
                setZoomImg={setZoomImg} 
              />
            } />
            
            {/* NOWE ROUTES - SEO & PLATE AGGREGATION */}
            <Route path="/tablica/:plate" element={
              <PlateAggregation 
                showToast={showToast} 
                setReportId={setReportId} 
                setZoomImg={setZoomImg} 
              />
            } />
            <Route path="/poradnik/:identifier" element={
              <SightingDetail 
                showToast={showToast} 
                setZoomImg={setZoomImg} 
                isPoradnik={true}
              />
            } />
            
            {/* ZAKTUALIZOWANY - dual support (ID lub plateSlug) */}
            <Route path="/pojazd/:identifier" element={
              <SightingDetail 
                showToast={showToast} 
                setZoomImg={setZoomImg} 
              />
            } />
            
            <Route path="/add" element={<AddSighting showToast={showToast} />} />

            {/* Marketplace Routes */}
            <Route path="/market" element={<MarketHome />} />
            <Route path="/market/search" element={<SearchResults />} />
            <Route path="/market/car/:id" element={<ListingDetail showToast={showToast} setZoomImg={setZoomImg} />} />
            <Route path="/market/sell" element={<AddListing showToast={showToast} />} />
            <Route path="/market/my-listings" element={<MyListings showToast={showToast} />} />
            <Route path="/market/messages" element={<Messages showToast={showToast} />} />
            <Route path="/market/messages/:id" element={<Messages showToast={showToast} />} />

            {/* Shared Routes */}
            <Route path="/login" element={<Login showToast={showToast} />} />
            <Route path="/admin" element={<AdminPanel sightings={sightings} showToast={showToast} />} />
            <Route path="/regulamin" element={<Terms />} />
            
            {/* Legacy redirect */}
            <Route path="/sighting/:id" element={<Navigate to="/pojazd/:id" replace />} />

            {/* Dzban Roku Routes - only if feature enabled */}
            {FEATURES.DZBAN_ROKU && (
              <>
                <Route path="/dzban-roku" element={<DzbanHome showToast={showToast} />} />
                <Route path="/dzban-roku/ranking" element={<DzbanRanking />} />
                <Route path="/dzban-roku/miasta" element={<DzbanCities />} />
                <Route path="/dzban-roku/marki" element={<DzbanBrands />} />
              </>
            )}
          </Routes>

          {/* Mobile Bottom Nav */}
          <MobileNav hasUnreadAlerts={hasUnreadAlerts} />
        </main>
      </div>
    </Router>
  );
}